/* eslint-disable no-magic-numbers */
import { useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { getReactionsForModel, updateReactionLine } from '@/redux/reactions/reactions.thunks';
import { showToast } from '@/utils/showToast';
import { reactionMetaStateSelector } from '@/redux/modal/modal.selector';
import { reactionsByModelIdSelector } from '@/redux/reactions/reactions.selector';
import { UpdateReactionLineData } from '@/redux/reactions/reactions.types';
import { getReactionLines } from '@/components/FunctionalArea/Modal/EditReactionModal/utils/getReactionLines';
import {
  MAX_LINE_WIDTH,
  MIN_LINE_WIDTH,
} from '@/components/FunctionalArea/Modal/LayerLineFactoryModal/LayerLineFactory.constants';

/**
 * Owns the line styling (width, color, type) of the edited reaction. The
 * reaction is styled as a whole: one set of controls, seeded from the reaction
 * line, and every edit is applied to all of its lines - the reaction line plus
 * the lines of each reactant, product, modifier and operator. Each of those has
 * its own endpoint, so a single edit fans out into one request per line.
 *
 * Width is a free-text number input, so it is mirrored in local state while the
 * user types and only written on blur; color and line type are pickers and
 * persist immediately.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useReactionLines = () => {
  const dispatch = useAppDispatch();

  const reactionMetaState = useAppSelector(reactionMetaStateSelector);
  const modelId = reactionMetaState?.model;
  const reactions = useAppSelector(state => reactionsByModelIdSelector(state, modelId || 0));
  const reaction = reactions.find(item => item.id === reactionMetaState?.id);

  // The reaction line is the preset for the whole reaction.
  const presetLine = reaction?.line;

  const [isSending, setIsSending] = useState<boolean>(false);
  const [width, setWidth] = useState<number>(presetLine?.width ?? MIN_LINE_WIDTH);

  const color = presetLine?.color;
  const lineType = presetLine?.lineType;

  const clampLineWidth = (value: number): number =>
    Math.min(MAX_LINE_WIDTH, Math.max(MIN_LINE_WIDTH, value));

  const saveAllLines = async (data: UpdateReactionLineData): Promise<void> => {
    if (!reaction || modelId === undefined) {
      return;
    }
    setIsSending(true);
    try {
      await Promise.all(
        getReactionLines(reaction).map(({ target, line }) =>
          dispatch(
            updateReactionLine({
              modelId,
              reactionId: reaction.id,
              target,
              line,
              data,
            }),
          ).unwrap(),
        ),
      );
      dispatch(getReactionsForModel(modelId));
    } catch (error) {
      const typedError = error as SerializedError;
      showToast({
        type: 'error',
        message: typedError.message || 'An error occurred while updating the lines.',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleWidthBlur = (): void => {
    if (width !== presetLine?.width) {
      saveAllLines({ width });
    }
  };

  return {
    hasLines: Boolean(reaction),
    isSending,
    width,
    setWidth,
    clampLineWidth,
    handleWidthBlur,
    color,
    lineType,
    saveAllLines,
  };
};
